import { gql } from '@apollo/client';
import { EditorState, RawDraftContentState } from 'draft-js';

import {
  ILinkPreview,
  findFirstLinkInBody,
  findFirstLinkInEditor,
} from '@/modules/draft-js-link-plugin';

import { useLinkPreviewFragment } from './graphql-types/useLinkPreviewFragment';

type IRawContentOptions = {
  rawDraftContentState: useLinkPreviewFragment | undefined;
  onHidePreviewHandler?: (
    newRawDraftContentState: RawDraftContentState
  ) => void;
};

type IEditorContentOptions = {
  editorState: EditorState;
  onHidePreviewHandler: (newEditorState: EditorState) => void;
};

const DRAFT_JS_FRAGMENT = gql`
  fragment useLinkPreviewFragment on DraftJS_ContentState {
    ...RawDraftContentStateFragment
  }
`;

export const useLinkPreview = (
  options: IRawContentOptions | IEditorContentOptions
): {
  linkPreview?: ILinkPreview;
  hidePreview?: () => void;
} => {
  if (!('rawDraftContentState' in options || 'editorState' in options)) {
    return {
      linkPreview: undefined,
      hidePreview: undefined,
    };
  }

  const link =
    'rawDraftContentState' in options
      ? findFirstLinkInBody(options.rawDraftContentState)
      : findFirstLinkInEditor(options.editorState);

  if (!link || (link && link[0].hideLinkPreview)) {
    return { linkPreview: undefined, hidePreview: undefined };
  }

  const linkMetaData = link[0];
  const linkPreview = link[1];

  const hidePreview = (): void => {
    if ('rawDraftContentState' in options) {
      const newContentState = JSON.parse(
        JSON.stringify(options.rawDraftContentState)
      );

      newContentState.entityMap[linkMetaData.key].data.hideLinkPreview = true;
      if (!!options.onHidePreviewHandler) {
        options.onHidePreviewHandler(newContentState);
      }
    }

    if ('editorState' in options) {
      const contentState = options.editorState.getCurrentContent();
      const newContentState = contentState.mergeEntityData(linkMetaData.key, {
        hideLinkPreview: true,
      });

      options.onHidePreviewHandler(
        EditorState.createWithContent(newContentState)
      );
    }
  };

  return { linkPreview, hidePreview };
};

useLinkPreview.rawDraftContentStateFragment = DRAFT_JS_FRAGMENT;
